/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import Link from 'components/Link';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import { selectCreditApplication } from 'state/creditApplication';

import Button from 'components/Button';
import PageContainer from 'components/PageContainer';
import { FormTextField } from 'components/TextField';

import { FormValues } from './OtpVerification';
import ConsumerSearchContext from '../ConsumerSearchContext';

import styles from './styles';
import CancelButton from 'features/CancelButton';
import IovationButton from 'features/IovationButton';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import SkipPrefillButton from 'features/SkipPrefillButton';
import Title from 'components/Title';
import { selectTenant } from 'state/tenant';

type PropTypes = FormikProps<FormValues> & {
  resendSms: () => Promise<unknown>;
  attemptsRemaining: string | null;
  tooManyAttempts: boolean;
  timedOut: boolean;
};

const OtpVerificationView: React.FC<PropTypes> = props => {
  const {
    isValid,
    isSubmitting,
    resendSms,
    attemptsRemaining,
    tooManyAttempts,
    timedOut,
    resetForm,
  } = props;

  const { i18nPrefix, skipToPath, hasExistingAccount } = useContext(
    ConsumerSearchContext,
  );
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const {
    enabledFeatures: { requireAdditionalVerification },
  } = useSelector(selectTenant);

  const handleResend = useCallback(async () => {
    await resendSms();
    resetForm();
  }, [resendSms, resetForm]);

  const creditApplication = useSelector(selectCreditApplication);
  const mobilePhone = creditApplication.mobilePhone as string;

  const lastFourPhone = mobilePhone
    ? mobilePhone.substring(mobilePhone.length - 4)
    : null;

  const contextualTitleOptions = hasExistingAccount
    ? {
        context: 'existingAccount',
        lastFourPhone: lastFourPhone,
      }
    : {
        lastFourPhone: lastFourPhone,
      };

  const contextualBodyOptions = hasExistingAccount
    ? {
        context: 'existingAccount',
        lastFourPhone: lastFourPhone,
      }
    : {
        lastFourPhone: lastFourPhone,
      };

  return (
    <Form noValidate>
      <PageContainer maxWidth="sm">
        <div css={styles.headingContainer}>
          <Title>
            {tMd(`${i18nPrefix}otpVerification.title`, contextualTitleOptions)}
          </Title>
          <div css={styles.bodyText}>
            {tMdMultiline(
              `${i18nPrefix}otpVerification.body`,
              contextualBodyOptions,
            )}
          </div>
        </div>

        {timedOut ? (
          <div>
            <p css={styles.errorText}>
              {tMd(`${i18nPrefix}otpVerification.trackotpExpired`)}
            </p>
          </div>
        ) : null}

        <div css={styles.formFields}>
          <Field name="otpCode">
            {(fieldProps: FieldProps) => (
              <FormTextField
                id="otpCode"
                fieldProps={fieldProps}
                label={t(`formLabel.otpCode`)}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                }}
                error={Boolean(attemptsRemaining)}
                helperText={attemptsRemaining}
                disabled={tooManyAttempts || timedOut}
              />
            )}
          </Field>
        </div>

        <ActionButtonContainer>
          <IovationButton
            id="otpVerificationSubmit"
            type="submit"
            disabled={!isValid || isSubmitting || tooManyAttempts || timedOut}
            loading={isSubmitting}
          >
            {t(`${i18nPrefix}otpVerification.submit`)}
          </IovationButton>

          <Button id="resendOtp" onClick={handleResend} variant="text">
            {tMd(`${i18nPrefix}otpVerification.resendSubmit`)}
          </Button>

          {skipToPath && !requireAdditionalVerification ? (
            <SkipPrefillButton
              variant="text"
              id="continueWithoutSecureCode"
              component={Link}
              to={skipToPath}
            >
              {t(`${i18nPrefix}manualEntryButton`)}
            </SkipPrefillButton>
          ) : null}

          <CancelButton />
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
};

export default OtpVerificationView;
